import React from "react";
import numbro from "numbro";

/**
 * columns = {type: number | text, urlProp: false | string, target?: string, format?: numbrojs}
 * default values columns = {type: text, urlProp: false, target: "_blank", format: {thousandSeparated: true, mantissa: 2}}
 * check format protocol https://numbrojs.com/format.html
 * */

export function cuFormat({ headers, columns, links, countTitle, customFn = null }) {
  // This function basically takes array of headers and builds
  // custom scopedSlots object using parameters provided in columns
  return headers.reduce((acc, header, i) => {
    // formatting options that must be applied to the values of the current column
    const colFormattings = columns[i];
    // add scopedSlot if colFormattings object contains more than 1 formatting options
    if (!colFormattings) {
      return acc;
    }
    if (Object.keys(colFormattings).length >= 1) {
      // exposing some of formattings for simplicity
      const { urlProp: urlIndex, target, color: colColor } = colFormattings;
      acc = {
        ...acc,
        // each header should correspond to the function of cell
        // e.g.: (value, ...) => <td styles...>formatted(value)</td>
        // where each cell must be formatted according to parameters specified in columns
        [header]: (value, tdStyle = {}) => {
          // TODO: finds proper solution for this workaround, tdStyle is index on undefined countTitle
          const index = countTitle ? value[countTitle] - 1 : tdStyle;

          // all links that are used in the current row
          const rowLinks = urlIndex !== undefined && links[index];

          // current cell raw value
          const rawValue = value[header];

          // format column values based on current column parameters
          let formattedValue = getFormatted(colFormattings, rawValue);

          // get color of the cell from current column parameters
          const color = getColor(colColor, rawValue);

          return (
            <td key={header + i} style={{ color, ...tdStyle }}>
              {rowLinks && rowLinks.length ? (
                <a target={target || "_blank"} href={rowLinks[urlIndex]}>
                  {formattedValue}
                </a>
              ) : customFn ? (
                customFn(header, formattedValue, rawValue, value)
              ) : (
                formattedValue
              )}
            </td>
          );
        }
      };
    }
    return acc;
  }, {});
}

// get color of the text from backend. It can be either color code or some specific color function type
function getColor(color, value) {
  if (!color) return "";
  if (color === "color by value") return value <= 0 ? "rgb(199, 52, 62)" : "rgb(33, 233, 129)";
  return color;
}

function getFormatted({ type, format, prefix = false, suffix = false, dateFormat }, value) {
  let formatted = value;
  if (type === "text") {
    formatted = formatText(value, format);
  } else if (type === "number" || type === "numeric") {
    formatted = formatNumber(value, format);
  } else if (type === "date" && dateFormat && value !== "") {
    const date = new Date(value);
    formatted = new Intl.DateTimeFormat("en-US", {
      month: "numeric",
      day: "numeric",
      year: "numeric"
    }).format(date);
  }
  if (prefix) {
    formatted = prefix + formatted;
  }
  if (suffix) {
    formatted += suffix;
  }
  return formatted;
}

const RIGHT_TO_LEFT_REGEX =
  /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDCF\uFDF0-\uFDFF\uFE70-\uFEFF]/; // A regular expression to determine the string from right to left (for Arabic)

function fixNegativeDollarAmount(amount, currencySymbol) {
  const isRightToleftText = amount.search(RIGHT_TO_LEFT_REGEX) !== -1;

  if (amount.startsWith(`-${currencySymbol}`) && !isRightToleftText) {
    return `${currencySymbol}-` + amount.substring(currencySymbol.length + 1);
  }
  return amount;
}

function formatNumber(value, format) {
  if (typeof value === "number") {
    return fixNegativeDollarAmount(numbro(value).format(format || {}), format?.currencySymbol);
  }
  return value;
}

function formatText(value) {
  return value;
}

function arrSum(arr) {
  const sum = arr.reduce((acc, res) => acc + res, 0);
  return Math.round(sum * 100) / 100;
}

export const calculateTotals = (data, calculations, headers, currentHeader) => {
  let result = 0;
  const values = data.map((row) => row[currentHeader]);

  if (calculations.type === "sum") {
    result = arrSum(values);
  } else if (calculations.type === "average") {
    const totals = calculations.rows
      .map((row) => headers[row])
      .map((header) => arrSum(data.map((row) => row[header])));
    result = totals[1] / totals[0];
  } else if (calculations.type === "100%") {
    result = 1;
  }

  return isNaN(result) || result === Infinity ? 0 : result;
};
